import { ContextKindKey } from '@gonfalon/contexts/types';
import { getRuleKind } from '@gonfalon/flags/getRuleKind';
import { isMobileRule } from '@gonfalon/flags/isMobileRule';
import { fromJS, List, Record } from '@gonfalon/immutable-shim/immutableShim';
import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';
import { v4 } from 'uuid';

import type { Clause } from '#utils/clause/records/Clause';
import { createClause } from '#utils/clause/records/Clause';
import type { VariationOrRollout } from '#utils/flags/flagTypes';
import { ReleasePhaseConfig } from '#utils/flags/records/ReleasePhaseConfig';
import type { Rollout } from '#utils/flags/records/Rollout';
import { createRollout } from '#utils/flags/records/Rollout';

export type RuleProps = {
  _id?: string;
  _key?: string;
  ref?: string;
  disabled?: boolean;
  clauses: List<Clause>;
  trackEvents: boolean;
  isEditing: boolean;
  description?: string;
  measuredRolloutConfig?: ReleasePhaseConfig;
} & VariationOrRollout;

export class Rule extends Record<RuleProps>({
  _id: undefined,
  _key: undefined,
  ref: '',
  disabled: false,
  clauses: List.of(createClause()),
  trackEvents: false,
  isEditing: false,
  description: '',
  variation: undefined,
  rollout: undefined,
  measuredRolloutConfig: undefined,
}) {
  toRep() {
    return fromJS(this.toJSON()).withMutations((map: Rule) => {
      map.remove('_key');
      map.get('rollout') === undefined ? map.remove('rollout') : map.update('rollout', (r?: Rollout) => r?.toRep());
      map.get('variation') === undefined && map.remove('variation');
      map.update('clauses', (cs: List<Clause>) => cs.map((c) => c.toRep()));
      map.get('description');
    });
  }

  hasNonUserContextClause() {
    return this.clauses.some((c) => !!c.contextKind && c.contextKind !== ContextKindKey.USER);
  }

  hasSegment(segmentKey: string) {
    return this.clauses.some((c) => c.hasSegment(segmentKey));
  }

  isMobileRule() {
    return isMobileRule(this.toJS());
  }
  getRuleKind() {
    return getRuleKind(this.toJS());
  }
}

export function createRule(props: CreateFunctionInput<Rule> = {}) {
  const rule = props instanceof Rule ? props : new Rule(fromJS(props));
  // Use the API ref if it exists, fallback to _key, then generate a new uuid.
  const key = rule.ref || rule._key || v4();

  return rule.withMutations((map) => {
    map.set('_key', key);
    map.set('ref', key);
    map.update('clauses', (cs) => cs.map(createClause));
    map.update('rollout', (r) => (r ? createRollout(r) : r));
    map.update('measuredRolloutConfig', (m) => (m ? new ReleasePhaseConfig(m) : m));
  });
}
